// @flow

// Libs
import React, { PureComponent } from 'react'
import _get from 'lodash/get'
import _map from 'lodash/map'

// Utils
import traverse from 'fc-utils/traverse'
import convert from 'convert-units'

// Model properties
import { PROP_SHAPE } from 'fc-model-assets'
import { PROP_SHAPE_DATA } from 'fc-model-shapes'
import { GRID_UNIT_ACRES, GRID_UNIT_HECTARES } from 'hfty-model-grid'

// Components
import ShapeSvg from 'fc-components-shapes/src/ShapeSvg'
import LockIcon from 'fc-icons/src/Lock'
import LockOpenIcon from 'fc-icons/src/LockOpen'
import Typography from '@react-mdc/typography'
import Textfield from '@react-mdc/textfield'
import Formfield from '@react-mdc/form-field'

// Turf
import turfArea from '@turf/area'

// Styles
import { dark, primary, white } from 'hfty-styles/src/colors'
import './assetPanel.scss'

// Local Utils
// For <Feature />
const getLabel = traverse(['feature', 'properties', 'label'], { default: '' })
const getDescription = traverse(['feature', 'properties', 'description'], { default: '' })
const getFeatureIndex = traverse(['feature', 'properties', 'featureIndex'])

// Local Styles
const styles = {
  featureItem: ({ isHovering, isSelected }: { isHovering: boolean, isSelected: boolean }): Object => ({
    flex: '0 0 auto',
    display: 'flex',
    padding: 10,
    backgroundColor: isHovering ? dark(0.8) : isSelected ? primary() : white(),
    color: isHovering ? white() : isSelected ? white() : dark(),
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: '15px',
    boxShadow: '0 2px 2px rgba(0,0,0,0.1)',
  }),
  featureItemSvg: {
    flex: '0 0 45px',
    height: 45,
    width: 45,
    marginRight: 15,
  },
  infoContainer: {
    flex: '1 1 auto',
  },
}

export function JobFeatureLock({ isLocked }: boolean): React.element<any> {
  const icon = !isLocked ? <LockIcon color={'#3C3C3C'} /> : <LockOpenIcon color={'#3C3C3C'} />

  return icon
}

// Local Components
type FeatureProps = {
  index: number,
  feature: Object,
  isHovering: boolean,
  isSelected: boolean,
  isEditing: boolean,
  featureOperation: ?Object,
  selectedFieldId: number,
  isJobFeature: boolean,
  // isEditingField: boolean,
  // isEditingFeature: boolean,
  fitMapBounds: Function,
  onSelectFeature: Function,
  onToggleHoverFeature: Function,
  onChangeFeature: Function,
}

export class Feature extends PureComponent {
  props: FeatureProps
  onClick: Function
  onMouseEnter: Function
  onMouseLeave: Function

  constructor(props: FeatureProps) {
    super(props)

    this.onClick = this.onClick.bind(this)
    this.onMouseEnter = this.onMouseEnter.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
  }

  label({ feature, index }: { feature?: Object, index?: number } = {}): string {
    if (!feature) {
      return getLabel(this.props) || `Feature ${this.props.index + 1}`
    }

    const label = feature.properties.label

    if (label) {
      return label
    } else if (!index) {
      return `Feature ${this.props.index + 1}`
    }

    return `Feature ${this.props.index + 1}.${index}`
  }

  area({ feature, operation }: Object): React.Element<any> {
    let unit = ''
    if (operation) {
      unit = _get(operation, 'model.gridData.grid.units', GRID_UNIT_ACRES)
    } else {
      unit = _get(feature, '0.units', GRID_UNIT_ACRES)
    }
    const conv = unit === GRID_UNIT_HECTARES ? 'ha' : 'ac'
    return (
      <span>
        {convert(turfArea(feature)).from('m2').to(conv).toFixed(2)}
        {conv}
      </span>
    )
  }

  onClick() {
    try {
      const [minX, minY, maxX, maxY] = this.props.feature.bbox
      this.props.fitMapBounds([
        [minY, minX],
        [maxY, maxX],
      ])
    } catch (error) {
      console.warn('Unable to zoom to feature.')
    }

    if (this.props.isSelected) {
      this.props.onSelectFeature(null)
    } else {
      this.props.onSelectFeature(getFeatureIndex(this.props))
    }
  }

  onMouseEnter() {
    this.props.onToggleHoverFeature(getFeatureIndex(this.props))
  }

  onMouseLeave() {
    this.props.onToggleHoverFeature(null)
  }

  render(): React.Element<any> {
    const description = getDescription(this.props)

    return (
      <div
        key={0}
        style={styles.featureItem({
          isHovering: this.props.isHovering,
          isSelected: this.props.isSelected,
        })}
        onClick={this.onClick}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <div style={styles.featureItemSvg}>
          <ShapeSvg width={45} height={45} preserveAspectRatio="xMidYMid meet" geoJSON={this.props.feature} />
        </div>
        <Typography style={styles.infoContainer}>
          <Typography.Text textStyle="body2">{this.label()}</Typography.Text>
          <Typography.Text textStyle="caption">{description}</Typography.Text>
          <Typography.Text textStyle="caption">Area: {this.area(this.props)}</Typography.Text>
        </Typography>
        {this.props.isJobFeature ? <JobFeatureLock isLocked={this.props.feature.isLocked} /> : null}
      </div>
    )
  }
}

export class FeatureForm extends PureComponent {
  props: FeatureProps
  onChangeLabel
  onChangeDescription

  constructor(props: FeatureProps) {
    super(props)

    this.onChangeLabel = this.onChangeLabel.bind(this)
    this.onChangeDescription = this.onChangeDescription.bind(this)
  }

  label({ feature, index }: { feature?: Object, index?: number } = {}): string {
    if (!feature) {
      return getLabel(this.props) || `Feature ${this.props.index + 1}`
    }

    const label = feature.properties.label

    if (label) {
      return label
    } else if (!index) {
      return `Feature ${this.props.index + 1}`
    }

    return `Feature ${this.props.index + 1}.${index}`
  }

  area({ feature, operation }: Object): React.Element<any> {
    let unit = ''
    if (operation) {
      unit = _get(operation, 'model.gridData.grid.units', GRID_UNIT_ACRES)
    } else {
      unit = _get(feature, '0.units', GRID_UNIT_ACRES)
    }
    const conv = unit === GRID_UNIT_HECTARES ? 'ha' : 'ac'
    return (
      <span>
        {convert(turfArea(feature)).from('m2').to(conv).toFixed(2)}
        {conv}
      </span>
    )
  }

  onChangeLabel(e: Event & { currentTarget: HTMLInputElement }) {
    this.onChangeProperty('label', e.currentTarget.value)
  }

  onChangeDescription(e: Event & { currentTarget: HTMLInputElement }) {
    this.onChangeProperty('description', e.currentTarget.value)
  }

  onChangeProperty(property: string, value: string) {
    this.props.onChangeFeature({
      ...this.props.feature,
      properties: {
        ...this.props.feature.properties,
        [property]: value,
      },
    })
  }

  render(): React.Element<any> {
    return (
      <div
        key={0}
        style={styles.featureItem({
          isHovering: this.props.isHovering,
          isSelected: this.props.isSelected,
        })}
        onClick={this.onClick}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <div style={styles.featureItemSvg}>
          <ShapeSvg width={45} height={45} preserveAspectRatio="xMidYMid meet" geoJSON={this.props.feature} />
        </div>
        <div>
          <Formfield>
            <Textfield className={this.props.isSelected ? 'selectedLabel' : ''}>
              <Textfield.Input placeholder={'Label'} value={getLabel(this.props)} onChange={this.onChangeLabel} />
            </Textfield>
          </Formfield>
          <Formfield>
            <Textfield className={this.props.isSelected ? 'selectedLabel' : ''}>
              <Textfield.Input
                placeholder={'Description'}
                value={getDescription(this.props)}
                onChange={this.onChangeDescription}
              />
            </Textfield>
          </Formfield>
        </div>
      </div>
    )
  }
}

// Exports
type Props = {
  features: Array<Object>,
  featureOperation: ?Object,
  selectedFeatureIndex: number,
  hoverFeatureIndex: number,
  selectedFieldId: number,
  fitMapBounds: Function,
  onSelectFeature: Function,
  onToggleHoverFeature: Function,
  onEditFeature: Function,
  onChange: Function,
}

export default class FeaturesOfField extends PureComponent {
  props: Props

  constructor(props: Props) {
    super(props)

    this.onChangeFeature = this.onChangeFeature.bind(this)
  }

  onChangeFeature(feature: Object) {
    this.props.onChange({
      ...this.props.featureOperation,
      model: {
        ...this.props.featureOperation.model,
        [PROP_SHAPE]: {
          ...this.props.featureOperation.model[PROP_SHAPE],
          [PROP_SHAPE_DATA]: {
            ...this.props.featureOperation.model[PROP_SHAPE][PROP_SHAPE_DATA],
            features: _map(
              this.props.featureOperation.model[PROP_SHAPE][PROP_SHAPE_DATA].features,
              function (currentFeature: Object): Object {
                if (currentFeature.properties.featureIndex === feature.properties.featureIndex) {
                  return feature
                }

                return currentFeature
              }
            ),
          },
        },
      },
    })
  }

  render(): Array<React.Element<any>> {
    const isEditing = this.props.featureOperation
    const FeatureComponent = isEditing ? FeatureForm : Feature

    return _map(this.props.features, (feature: Object, index: number): React.Element<any> => {
      const isSelected = this.props.selectedFeatureIndex === feature.properties.featureIndex
      const isHovering = this.props.hoverFeatureIndex === feature.properties.featureIndex

      return (
        <FeatureComponent
          // Data
          feature={feature}
          featureOperation={this.props.featureOperation}
          selectedFieldId={this.props.selectedFieldId}
          key={index}
          index={index}
          // Flags
          isSelected={isSelected}
          isHovering={isHovering}
          isEditing={isEditing}
          // Actions
          fitMapBounds={this.props.fitMapBounds}
          onSelectFeature={this.props.onSelectFeature}
          onToggleHoverFeature={this.props.onToggleHoverFeature}
          onEditFeature={this.props.onEditFeature}
          onChangeFeature={this.onChangeFeature}
        />
      )
    })
  }
}
